// useStudioGesture — the Studio canvas's pointer gestures (Studio.tsx): drag
// a card (a move, or a drop on another card: studioDrop.ts), pan the view,
// or draw the Container tool's box. The gesture in progress is view state;
// everything it decides goes out through the canvas's intents.
//
//   - a drag starts past DRAG_THRESHOLD pixels, so a click stays a click (it
//     selects; shift-click adds or removes);
//   - while a card hovers over one it would combine with, ↑/↓ switch the new
//     container's kind (`switchKind`, from the canvas's keys);
//   - held over a folded container for UNFOLD_AFTER_MS it unfolds it, in
//     place and for good (nothing folds itself back);
//   - a plain click on the empty canvas clears the selection (while
//     something is in focus the desk is frozen: StudioDesk.tsx).

export { useStudioGesture };

import {
    type Dispatch,
    type PointerEvent,
    type RefObject,
    type SetStateAction,
    useState,
} from "react";
import type { ContainerKind } from "../../app/containerFacts.js";
import type { Tool } from "../StudioToolbar/studioTools.js";
import { boxBetween, cardsInBox } from "./studioBox.js";
import type { StudioCard as Card } from "./studioCards.js";
import {
    applyDrop,
    type DropCase,
    dropAt,
    dropTargetOf,
    NO_DROP,
    switchKind,
} from "./studioDrop.js";
import type { Placement, Point, StudioView } from "./studioLayout.js";

type CardGesture = {
    readonly type: "card";
    readonly id: string;
    readonly pointer: Point;
    readonly origin: Point;
    readonly moved: boolean;
    readonly at: Point;
    readonly additive: boolean;
    // The pointer on the canvas, and the cards at their drawn sizes: what is
    // under it is where a drop goes (studioDrop.ts).
    readonly hover: Point;
    readonly drawn: ReadonlyMap<string, Placement>;
    // ↑/↓ pressed an odd number of times: the other kind of container.
    readonly switched: boolean;
    // The folded container under the pointer, and since when (event time).
    readonly over: { readonly id: string; readonly since: number } | null;
};

// A pointer gesture in progress.
type Gesture =
    | CardGesture
    | {
          readonly type: "pan";
          readonly pointer: Point;
          readonly origin: Point;
          readonly moved: boolean;
      }
    // The container tool's box, in viewport pixels.
    | {
          readonly type: "box";
          readonly from: Point;
          readonly to: Point;
          readonly moved: boolean;
          // Where each card is, at the size it is drawn (the layout's sizes
          // are estimates), measured when the box starts.
          readonly drawn: ReadonlyMap<string, Placement>;
      };

const DRAG_THRESHOLD = 3;
const UNFOLD_AFTER_MS = 600;

const distance = (a: Point, b: Point): number =>
    Math.hypot(a.x - b.x, a.y - b.y);

type GestureDeps = {
    readonly viewport: RefObject<HTMLDivElement | null>;
    readonly root: RefObject<HTMLDivElement | null>;
    readonly view: StudioView;
    readonly setView: Dispatch<SetStateAction<StudioView>>;
    readonly tool: Tool;
    readonly setTool: (tool: Tool) => void;
    readonly cards: readonly Card[];
    readonly placements: ReadonlyMap<string, Placement>;
    readonly folded: ReadonlySet<string>;
    readonly containerKind: ContainerKind;
    readonly intents: {
        readonly onToggleFold: (id: string) => void;
        readonly putInNew: (
            kind: ContainerKind,
            ids: readonly string[],
            at: Point,
        ) => void;
        readonly onMove: (id: string, at: Point) => void;
        readonly onAddToContainer: (
            containerId: string,
            ids: readonly string[],
        ) => void;
        readonly onToggleSelected: (id: string) => void;
        readonly onSelectOnly: (id: string) => void;
        readonly onClearSelection: () => void;
    };
};

const useStudioGesture = ({
    viewport,
    root,
    view,
    setView,
    tool,
    setTool,
    cards,
    placements,
    folded,
    containerKind,
    intents,
}: GestureDeps) => {
    const [gesture, setGesture] = useState<Gesture | null>(null);
    const { zoom, pan } = view;

    const pointOf = (e: { clientX: number; clientY: number }): Point => ({
        x: e.clientX,
        y: e.clientY,
    });
    // A pointer in viewport pixels, and a viewport point on the canvas.
    const localOf = (e: { clientX: number; clientY: number }): Point => {
        const box = viewport.current?.getBoundingClientRect();
        return {
            x: e.clientX - (box?.left ?? 0),
            y: e.clientY - (box?.top ?? 0),
        };
    };
    const toCanvas = (p: Point): Point => ({
        x: (p.x - pan.x) / zoom,
        y: (p.y - pan.y) / zoom,
    });
    // The placements at the cards' drawn sizes (the plane is unscaled
    // inside, so an offset size is in canvas units).
    const drawnPlacements = (): ReadonlyMap<string, Placement> =>
        new Map(
            [...placements].map(([id, p]) => {
                const el = viewport.current?.querySelector<HTMLElement>(
                    `[data-card-id="${CSS.escape(id)}"]`,
                );
                return [
                    id,
                    el == null
                        ? p
                        : {
                              ...p,
                              width: el.offsetWidth,
                              height: el.offsetHeight,
                          },
                ];
            }),
        );

    const pending =
        gesture?.type === "card" && gesture.moved
            ? dropAt(cards, gesture.id, gesture.hover, gesture.drawn)
            : NO_DROP;
    const drop: DropCase =
        gesture?.type === "card" && gesture.switched
            ? switchKind(pending)
            : pending;

    // The container box: on screen, and the cards fully inside it.
    const screenBox =
        gesture?.type === "box" && gesture.moved
            ? boxBetween(gesture.from, gesture.to)
            : null;
    const topLeft: Point = { x: screenBox?.x ?? 0, y: screenBox?.y ?? 0 };
    const boxed =
        screenBox === null
            ? []
            : cardsInBox(
                  boxBetween(
                      toCanvas(topLeft),
                      toCanvas({
                          x: screenBox.x + screenBox.width,
                          y: screenBox.y + screenBox.height,
                      }),
                  ),
                  cards.map((c) => c.id),
                  gesture?.type === "box" ? gesture.drawn : placements,
              );

    // The empty canvas (or anything, with the hand tool): pan. React bubbles
    // a portal's events (an open card menu) to here: only a press on the
    // canvas itself pans, never one on a card menu.
    const startPan = (e: PointerEvent<HTMLDivElement>) => {
        const target = e.target;
        if (e.button !== 0 || !(target instanceof Element)) return;
        if (!e.currentTarget.contains(target)) return;
        if (target.closest(".bilda-row-overflow") !== null) return;
        // The canvas takes focus, so its keys work after a click on it.
        if (target.closest(".bilda-studio__card") === null)
            root.current?.focus({ preventScroll: true });
        if (tool === "container") {
            e.currentTarget.setPointerCapture(e.pointerId);
            const at = localOf(e);
            setGesture({
                type: "box",
                from: at,
                to: at,
                moved: false,
                drawn: drawnPlacements(),
            });
            return;
        }
        if (tool === "select" && target.closest(".bilda-studio__slot") !== null)
            return;
        e.currentTarget.setPointerCapture(e.pointerId);
        setGesture({
            type: "pan",
            pointer: pointOf(e),
            origin: pan,
            moved: false,
        });
    };

    const startCardDrag = (card: Card, e: PointerEvent<HTMLDivElement>) => {
        if (tool !== "select" || e.button !== 0) return;
        const at = placements.get(card.id);
        if (at === undefined) return;
        // The card owns this gesture; the canvas must not pan.
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        setGesture({
            type: "card",
            id: card.id,
            pointer: pointOf(e),
            origin: { x: at.x, y: at.y },
            moved: false,
            at: { x: at.x, y: at.y },
            additive: e.shiftKey,
            hover: toCanvas(localOf(e)),
            drawn: drawnPlacements(),
            switched: false,
            over: null,
        });
    };

    // A dragged card held over a folded container: since when; long enough,
    // and it unfolds.
    const overFolded = (
        g: CardGesture,
        hover: Point,
        drawn: ReadonlyMap<string, Placement>,
        now: number,
    ): CardGesture["over"] => {
        const target = dropTargetOf(dropAt(cards, g.id, hover, drawn));
        if (target === null || !folded.has(target)) return null;
        if (g.over?.id !== target) return { id: target, since: now };
        if (now - g.over.since < UNFOLD_AFTER_MS) return g.over;
        intents.onToggleFold(target);
        return null;
    };

    const onPointerMove = (e: PointerEvent) => {
        if (gesture === null) return;
        if (gesture.type === "box") {
            const to = localOf(e);
            setGesture({
                ...gesture,
                to,
                moved:
                    gesture.moved ||
                    distance(to, gesture.from) > DRAG_THRESHOLD,
            });
            return;
        }
        const p = pointOf(e);
        const moved =
            gesture.moved || distance(p, gesture.pointer) > DRAG_THRESHOLD;
        const dx = p.x - gesture.pointer.x;
        const dy = p.y - gesture.pointer.y;
        if (gesture.type === "pan") {
            if (moved)
                setView({
                    zoom,
                    pan: { x: gesture.origin.x + dx, y: gesture.origin.y + dy },
                });
            setGesture({ ...gesture, moved });
            return;
        }
        const hover = toCanvas(localOf(e));
        // Measured again: a container may have unfolded under the card.
        const drawn = drawnPlacements();
        setGesture({
            ...gesture,
            moved,
            hover,
            drawn,
            over: overFolded(gesture, hover, drawn, e.timeStamp),
            at: moved
                ? {
                      x: gesture.origin.x + dx / zoom,
                      y: gesture.origin.y + dy / zoom,
                  }
                : gesture.at,
        });
    };

    const onPointerUp = () => {
        if (gesture === null) return;
        setGesture(null);
        if (gesture.type === "box") {
            // Released: the boxed cards go into a new container of the
            // chosen kind, then Select.
            setTool("select");
            if (boxed.length > 0)
                intents.putInNew(containerKind, boxed, toCanvas(topLeft));
            return;
        }
        if (gesture.type === "pan") {
            if (!gesture.moved) {
                intents.onClearSelection();
            }
            return;
        }
        // A drop on another card combines them; anywhere else is a move.
        if (gesture.moved)
            applyDrop(drop, placements, {
                move: () => intents.onMove(gesture.id, gesture.at),
                putInNew: intents.putInNew,
                addToContainer: intents.onAddToContainer,
            });
        else if (gesture.additive) intents.onToggleSelected(gesture.id);
        else intents.onSelectOnly(gesture.id);
    };

    return {
        gesture,
        // Esc: drop it.
        cancel: () => setGesture(null),
        // ↑/↓ over a combining drop.
        switchKind: () => {
            if (gesture?.type === "card")
                setGesture({ ...gesture, switched: !gesture.switched });
        },
        drop,
        screenBox,
        boxed,
        localOf,
        toCanvas,
        startPan,
        startCardDrag,
        onPointerMove,
        onPointerUp,
        // Where a card is drawn: where it is dragged to, else its place.
        cardAt: (card: Card): Point | undefined =>
            gesture?.type === "card" && gesture.id === card.id && gesture.moved
                ? gesture.at
                : placements.get(card.id),
    };
};
